import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import { DOMAIN } from "../../../global";
import AptosNormal from "../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../utils/Customfonts/Aptos-Bold.ttf";
import { DateisValid } from "../../../utils/commonComponent/errorAlert";
import { DisciplineOptions } from "../../disciplineMaster/disiplineConst";
import { drawTables } from "./renderTables";
import { config, customFontFamily, loadAndAddFont } from "./certificateConfig";
import { getHeaderHeight, getFooterHeight, initDefaultPage, drawText } from "./commonTable";
import { summaryLable } from "../certConst";

let yPositionGlobal = 0;

export const certificateConfigData = {
  certificateProps: {}
};

// // SECTION 1: Customer Details Table
const drawCustomerDetails = (doc, pageWidth, cert) => {
  const totalPageWidth = pageWidth - config.contentMargin * 2;
  const disciplineLabel = DisciplineOptions.find((key) => key.id == cert?.inward_disciplineId)?.label || "";

  const customerDetailsData = [
    ["Customer Name", cert?.jobRegister_clientName || ""],
    ["Address of Customer", cert?.inward_address || ""],
    ["Principal Client", cert?.inward_principleClient || ""],
    ["Name of Project", cert?.jobRegister_projectName || ""],
    ["Discipline", disciplineLabel],
    ["Group", cert?.discipline_groupName || ""],
    ["Customer Reference", cert?.jobRegister_reference || ""],
    ["Source of Sample", cert?.inward_sampleDetails || ""],
    ["Type of Material Tested", cert?.materialScope_MaterialName || ""],
    ["Quantity of Samples Tested", cert?.inwardTests_quantity || ""],
    ["Condition of Sample", cert?.inward_sampleDetails || ""],
    ["Date of Sample Receipt", DateisValid(cert?.inward_inwardDate)],
    [
      "Period of Test",
      `${DateisValid(cert?.observationSheet_startTestingDate)} to ${DateisValid(cert?.observationSheet_endTestingDate)}`,
    ],
    ["Lab. Test Results", "As Per Attached Annexures"],
  ];

  autoTable(doc, {
    startY: yPositionGlobal,
    body: customerDetailsData,
    theme: "grid",
    styles: {
      fontSize: config.fontSize - 1,
      cellPadding: 3,
      halign: "left",
      valign: "middle",
      textColor: 0,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      fillColor: false,
      font: customFontFamily?.normalFont,
      fontStyle: customFontFamily?.normal,
    },
    columnStyles: {
      0: { cellWidth: totalPageWidth * 0.35, font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold },
      1: { cellWidth: totalPageWidth * 0.65 ,  font: customFontFamily?.normalFont, fontStyle: customFontFamily?.normal },
    },
    margin: {
      left: config.contentMargin,
      right: config.contentMargin,
      bottom: getFooterHeight() + 10,
    },
  });

  yPositionGlobal = doc.lastAutoTable.finalY;
};

// SECTION 2: Summary Report Table
const drawSummaryReport = (doc, pageWidth, cert, settings) => {
  const headers = cert?.inwardTestsHeaders || [];
  const summaryRows = cert?.inwardTestsList || [];

  if (summaryRows.length === 0 || headers.length === 0) return;

  const isVertical = settings?.["CertificateSummaryVertical"] === "true";
  if (isVertical) {
    // ── Vertical layout: test parameters as rows, samples as columns ──

    yPositionGlobal += 12;
    drawText(doc, summaryLable, pageWidth / 2, yPositionGlobal + 10, {
      bold: true,
      fontSize: config.fontSize,
      align: "center",
    });
    yPositionGlobal += 18;

    // Head row 1: fixed cols + "Bore Hole No" spanning all samples
    const headRow1 = [
      { content: "S.No", rowSpan: 2, styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" } },
      { content: "TEST PARAMETER", rowSpan: 2, styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" } },
      { content: "UNIT", rowSpan: 2, styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" } },
      { content: "Bore Hole No", colSpan: summaryRows.length, styles: { font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" } },
      { content: "TEST METHOD", rowSpan: 2, styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" } },
    ];

    // Head row 2: one cell per sample (boreHoleNo or index)
    const headRow2 = summaryRows.map((row, idx) => ({
      content: row.boreHoleNo || String(idx + 1),
      styles: { halign: "center", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, fontSize: config.fontSize - 3 },
    }));

    // Body: one row per header entry
    let snoCounter = 0;
    let lastTestId = null;
    const bodyRows = headers.map((header) => {
      const row = [];

      if (header.isFirstColumn) {
        snoCounter++;
        lastTestId = header.testId;
        // S.No
        row.push({
          content: String(snoCounter),
          rowSpan: header.columnSpan,
          styles: { halign: "center", valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold },
        });
        // TEST PARAMETER (testName)
        row.push({
          content: header.testName || "",
          rowSpan: header.columnSpan,
          styles: { font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, valign: "middle" },
        });
      }

      // UNIT
      row.push({ content: header.columnUnit || "-", styles: { halign: "center" } });

      // Data values — one per sample
      summaryRows.forEach((sampleRow) => {
        const val = sampleRow.readingByTestId?.[header.testId]?.[header.columnKey];
        row.push({ content: val || "-", styles: { halign: "center" } });
      });

      // TEST METHOD — only on first column of each test
      if (header.isFirstColumn) {
        row.push({
          content: header.testMethod || "-",
          rowSpan: header.columnSpan,
          styles: { halign: "center", valign: "middle", fontSize: config.fontSize - 3 },
        });
      }

      return row;
    });

    autoTable(doc, {
      startY: yPositionGlobal,
      head: [headRow1, headRow2],
      body: bodyRows,
      theme: "grid",
      styles: {
        fontSize: config.fontSize - 3,
        cellPadding: 3,
        halign: "center",
        valign: "middle",
        textColor: 0,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: false,
        font: customFontFamily?.normalFont,
        fontStyle: customFontFamily?.normal,
      },
      margin: {
        left: config.contentMargin,
        right: config.contentMargin,
        bottom: getFooterHeight() + 10,
      },
    });

    yPositionGlobal = doc.lastAutoTable.finalY;
    return;
  }
  // ── Horizontal layout (existing logic, untouched) ──
  
  // Group headers by testId for testName colSpan in first header row
  const testGroups = {};
  const testGroupOrder = [];
  headers.forEach((h) => {
    const key = String(h.testId);
    if (!testGroups[key]) {
      testGroups[key] = [];
      testGroupOrder.push(key);
    }
    testGroups[key].push(h);
  });

  // First header row: fixed cols (rowSpan=2) + testName groups (colSpan)
  const headRow1 = [
    {
      content: "Sr.No",
      rowSpan: 2,
      styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" },
    },
    {
      content: "Bore Hole No",
      rowSpan: 2,
      styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" },
    },
    {
      content: "Depth",
      rowSpan: 2,
      styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" },
    },
    {
      content: "Sample Type",
      rowSpan: 2,
      styles: { valign: "middle", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, halign: "center" },
    },
    ...testGroupOrder.map((testId) => ({
      content: testGroups[testId][0]?.testName || testGroups[testId][0]?.name || `Test ${testId}`,
      colSpan: testGroups[testId].length,
      styles: { halign: "center", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold, },
    })),
  ];

  // Second header row: column names under each testName
  const headRow2 = headers.map((h) => ({
    // content: h.columnName || h.name || "",
    content: `${h.columnName || ""}${
    h.columnUnit ? ` (${h.columnUnit})` : ""}`,
    styles: {
      halign: "center",
      font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold,
      fontSize: config.fontSize - 3,
    },
  }));

  // Body rows
  const bodyRows = summaryRows.map((row, idx) => [
    idx + 1,
    row.boreHoleNo || "-",
    `${row.fromDepth ? String(row.fromDepth).replace(/#/g, " ") : "-"} to ${row.toDepth ? String(row.toDepth).replace(/#/g, " ") : "-"}`,
    row.sampleType || "-",
    ...headers.map((h) => {
      // support both old single value and new column-keyed object
      const val = row.readingByTestId?.[h.testId];
      if (val && typeof val === "object") return val[h.columnKey] || "-";
      return val || "-";
    }),
  ]);

  // Summary heading
  yPositionGlobal += 12;
  drawText(doc, summaryLable, pageWidth / 2, yPositionGlobal + 10, {
    bold: true,
    fontSize: config.fontSize,
    align: "center",
  });
  yPositionGlobal += 18;

  autoTable(doc, {
    startY: yPositionGlobal,
    head: [headRow1, headRow2],
    body: bodyRows,
    theme: "grid",
    styles: {
      fontSize: config.fontSize - 3,
      cellPadding: 3,
      halign: "center",
      valign: "middle",
      textColor: 0,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      fillColor: false,
      font: customFontFamily?.normalFont,
      fontStyle: customFontFamily?.normal,
    },
    margin: {
      left: config.contentMargin,
      right: config.contentMargin,
      bottom: getFooterHeight() + 10,
    },
  });

  yPositionGlobal = doc.lastAutoTable.finalY;
};

// SECTION 3: First Page (Customer Details + Summary Report)
const drawFirstPage = async (doc, pageWidth, cert, isFirstPage, letterHead, settings) => {
  if (!isFirstPage) {
    doc.addPage();
  }
  initDefaultPage(doc, cert, letterHead);
  yPositionGlobal = getHeaderHeight() + config.headerSpacing;

  // Page 1: customer details
  drawCustomerDetails(doc, pageWidth, cert);

  // Remark + QR + image on same page after customer details
  yPositionGlobal = await drawBottomSection(
    doc,
    cert,
    yPositionGlobal + 10,
    pageWidth,
    letterHead,
    settings
  );

  // Summary Report starts on new page
  if(settings?.["CertificateSummaryReportPrint"] === "true" && cert?.inward_locations == 1) {
    doc.addPage();
    initDefaultPage(doc, cert, letterHead);
    yPositionGlobal = getHeaderHeight() + config.headerSpacing;
    
    drawSummaryReport(doc, pageWidth, cert, settings);
  }

  return yPositionGlobal;
};

// SECTION 4: Single Observation Sheet Info Table
const drawObsInfoTable = (doc, pageWidth, obs) => {
  const totalPageWidth = pageWidth - config.contentMargin * 2;
  const additionalEntries = Object.entries(obs?.observationSheet_additionalTestDetails || {});
  const additionalRows = [];
  for (let i = 0; i < additionalEntries.length; i += 2) {
    additionalRows.push([
      additionalEntries[i]?.[0] || "",
      additionalEntries[i]?.[1] || "",
      additionalEntries[i + 1]?.[0] || "",
      additionalEntries[i + 1]?.[1] || "",
    ]);
  }

  const obsInfoData = [
    ["Report No", { content: obs?.observationSheet_reportNumber || "", colSpan: 3 }],
    ["Job No", { content: obs?.jobRegister_jobNumber || "", colSpan: 3 }],
    [
      {
        content: [obs?.materialTestings_testName, obs?.materialTestings_testMethod]
          .filter(Boolean)
          .join("\n"),
        colSpan: 4,
        styles: { halign: "center", font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold },
      },
    ],
    ["Project", { content: obs?.jobRegister_projectName || "", colSpan: 3 }],
    [
      "Principal Client",
      obs?.inward_principleClient || "",
      "Date",
      DateisValid(obs?.observationSheet_startTestingDate),
    ],
    ["Client", { content: obs?.jobRegister_clientName || "", colSpan: 3 }],

    [
      "Location",
      obs?.inwardTests_locationChainageNo || "",
      "Bore Hole No",
      obs?.inwardTests_boreHoleNo || "",
    ],
    [
      "Length of Specimen (cm)",
      obs?.inwardTests_lengthOfRock || "",
      "Depth (m)",
      obs?.inwardTests_fromDepth || obs?.inwardTests_toDepth
        ? `${String(obs?.inwardTests_fromDepth || "").replace(/#/g, " ")} - ${String(obs?.inwardTests_toDepth || "").replace(/#/g, " ")}`
        : "",
    ],
    ["Condition of Test", obs?.inwardTests_sampleType || "", "", ""],
    // ADDED DYNAMIC FIELDS (no layout change)
    ...additionalRows,
  ];

  autoTable(doc, {
    startY: yPositionGlobal,
    body: obsInfoData,
    theme: "grid",
    styles: {
      fontSize: config.fontSize - 1,
      cellPadding: 3,
      halign: "left",
      valign: "middle",
      textColor: 0,
      lineWidth: 0.1,
      lineColor: [0, 0, 0],
      fillColor: false,
      font: customFontFamily?.normalFont,
      fontStyle: customFontFamily?.normal,
    },
    columnStyles: {
      0: { cellWidth: totalPageWidth * 0.25, font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold },
      1: { cellWidth: totalPageWidth * 0.25 },
      2: { cellWidth: totalPageWidth * 0.25, font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold },
      3: { cellWidth: totalPageWidth * 0.25 },
    },
    margin: {
      left: config.contentMargin,
      right: config.contentMargin,
      bottom: getFooterHeight() + 10,
    },
  });

  yPositionGlobal = doc.lastAutoTable.finalY + 5;
};

// SECTION 5: Single Observation Sheet Page (info table + static tables)
const drawSingleObsSheet = async (doc, pageWidth, cert, obs, letterHead, allSettings) => {
  // New page for every observation sheet
  doc.addPage();
  // Pass obs so the footer shows Tested By (obs-specific) + Reviewed By + Authorized By
  initDefaultPage(doc, cert, letterHead, obs, allSettings);
  yPositionGlobal = getHeaderHeight() + config.headerSpacing;

  // Obs info table
  drawObsInfoTable(doc, pageWidth, obs);

  // ── Static tables + reading rows for this obs sheet ──
  if (obs?.staticTables?.length > 0 && obs?.readingRows?.length > 0) {
    const props = {
      certificateDetails: [cert],
      margin: config.contentMargin,
      tableMargin: {
        left: config.contentMargin,
        right: config.contentMargin,
        top: getHeaderHeight() + 5,
        bottom: getFooterHeight() - 10,
      },
      // Pass obs here too so page breaks within this sheet also get obs footer
      initPage: () => initDefaultPage(doc, cert, letterHead, obs, allSettings),
    };

    yPositionGlobal = await drawTables(
      doc,
      yPositionGlobal + 5,
      obs.staticTables,
      obs.readingRows,
      props
    );
  }

  return yPositionGlobal;
};

// SECTION 6: Bottom Section (Note + Image + QR)
const drawBottomSection = async (doc, cert, yStart, pageWidth, letterHead, settings) => {
  const usableWidth = pageWidth - config.contentMargin * 2;
  let yPos = yStart;

  const remarkFontSize = config.fontSize - 2;
  const QR_SIZE = 50;
  const IMG_WIDTH = usableWidth * 0.2;
  const IMG_HEIGHT = 60;
  const sectionX = config.contentMargin;
  const QR_GAP = 10;
  const LABEL_FONT_SIZE = 7;
  const LABEL_GAP = 9;
  const QR_ROW_TOP_MARGIN = 10;

  // ─── Check if image exists
  const hasImage = cert?.observationSheet_fileAttachment && cert?.observationSheet_fileAttachment?.split(",")?.[0] &&
    /\.(jpg|jpeg|png|webp)$/i.test(cert?.observationSheet_fileAttachment?.split(",")?.[0]);

  // ─── Calculate remark width based on image presence 
  const remarkWidth = hasImage ? usableWidth * 0.65 : usableWidth;

  // ─── Estimate remark lines 
  let standardRemark = "";
  // Witness line 
  if (cert?.witnessString && settings?.["ObservationWitnessHistory"] === "true") {
    standardRemark += `The Test is Witnessed By: ${cert.witnessString}\n`;
  }
  standardRemark += `1. The laboratory studies and test results pertain only to the samples supplied by the customer.
2. S & R Geotechniques Pvt Ltd will not be responsible with respect to any claims, damages or losses from the use of above test report.
3. The above test results are related to the sample tested.
4. The test results cannot be used as evidence in any court or legal proceedings.
5. Test report shall not be reproduced except in full, without written approval by S & R Geotechniques Pvt Ltd
6. *As furnished by customer.`;

  let remarkLines = [];
  // if (cert?.observationSheet_configuration?.remark) {
    remarkLines = doc.splitTextToSize(
      // cert.observationSheet_configuration.remark,
      standardRemark,
      remarkWidth
    );
  // }

  // ─── Heights
  const noteBlockHeight =
    remarkLines.length > 0
      ? 15 + remarkLines.length * remarkFontSize * 1.2
      : 0;

  const qrRowHeight = QR_SIZE + LABEL_GAP + LABEL_FONT_SIZE + 4;

  const sectionHeight =
    noteBlockHeight +
    QR_ROW_TOP_MARGIN +
    qrRowHeight +
    (hasImage ? IMG_HEIGHT + 10 : 0);

  // ─── Page break if needed
  const pageHeight = doc.internal.pageSize.getHeight();
  const availableHeight = pageHeight - getFooterHeight() - yPos;

  if (sectionHeight > availableHeight) {
    doc.addPage();
    initDefaultPage(doc, cert, letterHead);
    yPos = getHeaderHeight() + config.headerSpacing;
  }

  // Note
  if (remarkLines.length > 0) {
    drawText(doc, "Note:", sectionX, yPos + 15, {
      bold: true,
      fontSize: config.fontSize,
    });
    drawText(doc, remarkLines, sectionX, yPos + 30, { fontSize: remarkFontSize });
  }

  // File Attachment Image
  if (hasImage) {
    const imgUrl = cert.observationSheet_fileAttachment.split(",")?.[0];
    try {
      const imgData = await loadImageAsBase64(imgUrl);
      const imgX = sectionX + usableWidth * 0.65 + 5;
      doc.addImage(imgData, "JPEG", imgX, yPos + 5, IMG_WIDTH, IMG_HEIGHT);
    } catch (e) {
      console.warn("Could not load attachment image:", e);
    }
  }

  // QR Code
  const qrRowY = yPos + noteBlockHeight + QR_ROW_TOP_MARGIN;
  // QR 1 — Certificate print (far LEFT)
  const qr1X = sectionX;
  try {
    const qrDataUrl1 = await QRCode.toDataURL(
      `${DOMAIN}/#/certificate/print/${cert?.id}`,
      { width: 120, margin: 1 }
    );
    doc.addImage(qrDataUrl1, "PNG", qr1X, qrRowY, QR_SIZE, QR_SIZE);
    drawText(doc, "Scan To Get Report", qr1X + QR_SIZE / 2, qrRowY + QR_SIZE + LABEL_GAP, { align: "center", fontSize: LABEL_FONT_SIZE });
  } catch (e) {
    console.warn("Could not generate QR code 1:", e);
  }

  // QR 2 — NABL certificate (far RIGHT)
  const qr2X = sectionX + usableWidth - QR_SIZE - 10;
  try {
    const qrDataUrl2 = await QRCode.toDataURL(`https://nablwp.qci.org.in/CertificateScopenew?x=VmSWOJQnmhIZrAXaua7hDA==&a=NzU3MzU=`,
      { width: 120, margin: 1 }
    );
    doc.addImage(qrDataUrl2, "PNG", qr2X, qrRowY, QR_SIZE, QR_SIZE);
    drawText(doc, "Scan For NABL Certificate", qr2X + QR_SIZE / 2, qrRowY + QR_SIZE + LABEL_GAP, { align: "center", fontSize: LABEL_FONT_SIZE });
  } catch (e) {
    console.warn("Could not generate QR code 2:", e);
  }

  // ─── Authorization Table 
  const authTableY = qrRowY + qrRowHeight + 2;
  const footerEntries = Object.entries(cert?.certificate_footer || {}).filter(([key]) =>
    ["preparedBy", "reviewedBy", "authorizedBy"].includes(key)
  );

  if (footerEntries.length > 0) {
    const authHead = [["Action/ Name", "Designation", "Date", "Signature"]];

    const authBody = await Promise.all(
      footerEntries.map(async ([key, value]) => {
        let sigCell = { content: "-", styles: { halign: "center", valign: "middle" } };

        if (value?.usersign) {
          try {
            const sigData = await loadImageAsBase64(value.usersign);
            sigCell = {
              content: "",
              styles: { halign: "center", valign: "middle", minCellHeight: 30 },
              _sigData: sigData, // carry for didDrawCell
            };
          } catch (e) {
            console.warn("Could not load signature image:", e);
          }
        }

        return [
          {
            content: `${value?.label || ""}\n${value?.userName || "-"}`,
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: value?.hrDesignation || "-",
            styles: { halign: "center", valign: "middle" },
          },
          {
            content: DateisValid(cert?.certificate_preparedDate) || "-",
            styles: { halign: "center", valign: "middle" },
          },
          sigCell,
        ];
      })
    );
    // Collect sig data before passing to autoTable (autoTable body can't hold custom props)
    const sigMap = {};
    authBody.forEach((row, rowIdx) => {
      const sigCell = row[3];
      if (sigCell?._sigData) {
        sigMap[rowIdx] = sigCell._sigData;
        delete sigCell._sigData;
      }
    });

    autoTable(doc, {
      startY: authTableY,
      head: authHead,
      body: authBody,
      theme: "grid",
      styles: {
        fontSize: config.fontSize - 1,
        cellPadding: 3,
        halign: "center",
        valign: "middle",
        textColor: 0,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: false,
        font: customFontFamily?.normalFont,
        fontStyle: customFontFamily?.normal,
      },
      headStyles: {
        fillColor: [240, 240, 240],
        font: customFontFamily?.boldFont, fontStyle: customFontFamily?.bold,
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: (pageWidth - config.contentMargin * 2) * 0.3 },
        1: { cellWidth: (pageWidth - config.contentMargin * 2) * 0.25 },
        2: { cellWidth: (pageWidth - config.contentMargin * 2) * 0.2 },
        3: { cellWidth: (pageWidth - config.contentMargin * 2) * 0.25 },
      },
      margin: {
        left: config.contentMargin,
        right: config.contentMargin,
        bottom: getFooterHeight(),
      },
      didDrawCell: (data) => {
        // Draw signature image inside the Signature column (col index 3), body rows only
        if (data.section === "body" && data.column.index === 3 && sigMap[data.row.index]) {
          const { x, y, width, height } = data.cell;
          const sigW = width * 0.8;
          const sigH = Math.min(height - 4, 25);
          const sigX = x + (width - sigW) / 2;
          const sigY = y + (height - sigH) / 2;
          doc.addImage(sigMap[data.row.index], "JPEG", sigX, sigY, sigW, sigH);
        }
      },
    });

    return doc.lastAutoTable.finalY;
  }

  return qrRowY + qrRowHeight;
};

// SECTION 7: Page Numbers
const drawPageNumbers = (doc) => {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    const pageText = `Page No: ${i} of ${pageCount.toString().padStart(2, "0")}`;
    const xPosition = pageWidth - config.margin;
    const yPosition = pageHeight - config.footerImageHeight - config.footerMargin + 10;

    drawText(doc, pageText, xPosition, yPosition, {
      align: "right",
      bold: true,
      fontSize: 9,
    });
  }
};

// HELPERS
const loadImageAsBase64 = (url) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/jpeg"));
    };
    img.onerror = reject;
    img.src = url;
  });

  // ─── Helper: Append external PDF pages using pdf-lib 
const appendPdfAttachment = async (doc, pdfUrl) => {
  try {
    const { PDFDocument } = await import("pdf-lib");
    // Step 1: Get current jsPDF as arraybuffer
    const currentArrayBuffer = doc.output("arraybuffer");
    // Step 2: Fetch external PDF
    const response = await fetch(pdfUrl);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const externalArrayBuffer = await response.arrayBuffer();
    // Step 3: Load both PDFs with pdf-lib
    const currentPdfDoc = await PDFDocument.load(currentArrayBuffer);
    const externalPdfDoc = await PDFDocument.load(externalArrayBuffer);
    // Step 4: Copy all pages from external PDF into current
    const pages = await currentPdfDoc.copyPages(externalPdfDoc, externalPdfDoc.getPageIndices());
    pages.forEach((page) => currentPdfDoc.addPage(page));
    // Step 5: Return merged as blob
    const mergedBytes = await currentPdfDoc.save();
    return new Blob([mergedBytes], { type: "application/pdf" });
  } catch (e) {
    console.error("appendPdfAttachment error:", e);
    return null;
  }
};

const downloadBlob = (blob, fileName) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
  document.body.removeChild(link);
};

// MAIN EXPORT
export const generateCertificatePDF = async ({ letterHead, draft, scanCopy, electronicSign, certificateDetails, setLoading, settings, allSettings }) => {
  try {
    setLoading(true);
    // Allow React to render the loader before heavy work
    await new Promise((resolve) => setTimeout(resolve, 100));

    certificateConfigData.certificateProps = {
      certificateDetails,
      letterHead,
      draft,
      scanCopy,
      electronicSign,
    };

    const doc = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", "AptosNormal", "normal"),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", "AptosBold", "bold"),
    ]);
    // Set default font
    doc.setFont("AptosNormal");
    doc.setFont("AptosBold");

    const pageWidth = doc.internal.pageSize.getWidth();

    for (let index = 0; index < certificateDetails.length; index++) {
      const cert = certificateDetails[index];
      yPositionGlobal = 0;

      // 1. First page: customer details + summary report
     await drawFirstPage(doc, pageWidth, cert, index === 0, letterHead, settings);

      // 2. Each observation sheet on its own new page
      if(settings?.["CertificateObserVationSheetPrint"] === "true") {
      const obsSheets = cert?.observationSheets || [];
      for (let obsIdx = 0; obsIdx < obsSheets.length; obsIdx++) {
        await drawSingleObsSheet(doc, pageWidth, cert, obsSheets[obsIdx], letterHead, allSettings);
      }
      }
    }

    // 4. Page numbers
    drawPageNumbers(doc);

    // 5. File name + save
    const fileName =
      certificateDetails.length === 1
      ? (({ materialScope_MaterialName: n, certificate_reportNumber: r }) =>
          n && r ? `${n}_${r}.pdf` : n || r ? `${n || r}.pdf` : "Certificates_Report.pdf"
        )(certificateDetails[0])
        : "Certificates_Report.pdf";

    // ── Check PDF attachment ──
    const pdfAttachmentUrl = certificateDetails
      ?.map((cert) => cert?.observationSheet_fileAttachment?.split(",")?.[0])
      ?.find((url) => url && /\.pdf$/i.test(url));

    if (pdfAttachmentUrl) {
      //  Merge attachment and download as blob
      const mergedBlob = await appendPdfAttachment(doc, pdfAttachmentUrl);
      if (mergedBlob) {
        downloadBlob(mergedBlob, fileName);
      } else {
        // Fallback: save without attachment
        doc.save(fileName);
      }
    } else {
      // No PDF attachment — save normally
      doc.save(fileName);
    }
  } catch (error) {
    console.error("Error generating certificate PDF:", error);
  } finally {
    setLoading(false);
  }
};